import { useMemo } from 'react'
import { useCurrentAccountContext } from '@retailer-platform/dashboard/utils'
import { useGetRegionsQuery } from '../../api'

/**
 * Fetches all regions via the PartnersService GraphQL API and returns a lookup
 * map of region ID to region name, or `null` if the user can't view regions.
 *
 * The mesh gates GetRegions on `partners_v4.view` plus all-partners full access;
 * calling it without both returns NOT_AUTHORIZED, so the query is skipped.
 *
 * The region list is small and stable, so it's fetched once and cached by Apollo.
 */
export const useRegionNames = (): Map<string, string> | null => {
  const { account } = useCurrentAccountContext()
  const canViewRegions = Boolean(
    account?.internalAccessConfiguration?.hasAllPartnersFullAccess &&
    account?.permissions?.some(p => p.slug === 'partners_v4.view')
  )
  const { data } = useGetRegionsQuery({ skip: !canViewRegions })

  return useMemo(() => {
    if (!canViewRegions) return null
    const map = new Map<string, string>()
    const regions = data?.instacart_customers_partners_v1_PartnersService_GetRegions?.regions
    if (regions) {
      for (const r of regions) {
        map.set(String(r.id), r.name ?? '')
      }
    }
    return map
  }, [data, canViewRegions])
}
